import React, { useState, useRef } from 'react';
import { Modal, Box, Typography, IconButton, Tabs, Tab, Button, CircularProgress } from '@mui/material';
import { CloseIcon, ContentCopyIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import { GmailIcon, OutlookIcon } from 'icons/brandIcons';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import { useParams } from 'react-router-dom';
import { copyEmailSignature } from 'utils/emailSignature/copyEmailSignature';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar'; // Make sure to import the Notification Snackbar component

const AddToEmailSignatureModal = ({ open, onClose, contentRef }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { cardId } = useParams();
  const { emailSignatureState } = useEmailSignatureContext();
  const qrRef = useRef(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const BASE_URL = `${FRONTEND_BASE_URL}/card`;
  const cardUrl = `${BASE_URL}/${cardId}`;

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };



  const handleCopySignature = async () => {
    setIsLoading(true);
    try {
      await copyEmailSignature({
        state: emailSignatureState[cardId],
        qrRef,
        contentRef,
        cardUrl: cardUrl
      });
      setNotification({
        open: true,
        message: 'Email signature copied to clipboard.',
        severity: 'info'
      });
      // alert('Email signature copied! Paste it into your email signature with Ctrl+V.');
    } catch (error) {
      setNotification({
        open: true,
        message: 'There was an error copying your signature. Please try again.',
        severity: 'error'
      });
      // alert('There was an error copying your signature. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: '75%', md: '60%', lg: '50%' },
    maxWidth: 800,
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    display: 'flex',
    flexDirection: 'column',
    p: { xs: 2, sm: 3, md: 5 },
  };

  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    bgcolor: 'rgba(255, 255, 255, 0.3)',
    zIndex: -1,
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: 8,
    right: 8,
  };

  const tabStyle = {
    textTransform: 'none',
    minWidth: 0,
    padding: '6px 12px',
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '&.Mui-selected': {
      color: '#2aaff7',
    },
    '&:hover': {
      color: '#2aaff7',
    },
    borderBottom: 'none',
  };

  const stepsContent = [
    {
      title: "Gmail",
      steps: [
        "Copy your email signature",
        "Within your Gmail account, click the gear icon on the top right, select the 'See all settings' option and scroll down to the 'Signature' section",
        "Select the 'Create new' option and paste your email signature into the text box",
        "In the 'Signature Defaults' subsection, select your newly created signature as default and then scroll down to the very bottom of the page and select the 'Save' button"
      ]
    },
    {
      title: "Outlook Web",
      steps: [
        "Copy your email signature",
        "Within your Outlook account, select the gear icon in the top right followed by the 'Compose and reply' option just below the 'Layout' tab at the top",
        "Click on 'New signature', provide a name for your signature and then paste your email signature into the larger text box",
        "In the 'Select default signatures' subsection below, choose your newly created signature as default and click the blue save button in the bottom right"
      ]
    },
    {
      title: "Outlook Desktop",
      steps: [
        "Copy your email signature",
        "(Windows) Within Outlook, select 'File' in the top left corner, followed by 'Options', then 'Mail'",
        "(Mac) Select 'Outlook' in the top left corner, then 'Preferences'",
        "Click on 'Signatures' in the menu/popup, set your newly created signature as default"
      ]
    },
    {
      title: "Mail Desktop",
      steps: [
        "Copy your email signature",
        "Within the Mail app, select 'Mail' in the top left corner, followed by 'Preferences', and then click on the 'Signatures' tab",
        "Paste your email signature into the text box on the right side and make sure the 'Always match my default message font' setting is unselected. If you don't have any existing signatures you will have to first create one by clicking on the '+' sign at the bottom",
        "Open the 'Choose Signature' popup and then select your newly created signature"
      ]
    },
  ];

  const renderContent = () => {
    const { steps } = stepsContent[selectedTab];
    return (
      <Box sx={{ mt: 2 }}>
        {steps.map((step, index) => (
          <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #f2f2f2', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#828282" }} variant="body2">
                STEP {index + 1}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.9rem' }}>{step}</Typography>
              {index === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  onClick={handleCopySignature}
                  sx={{
                    borderRadius: '20px',
                    color: 'white',
                    textTransform: 'none',
                    backgroundColor: '#2aaff7',
                    '&:hover': {
                      backgroundColor: '#1a8ac2',
                    },
                    minWidth: '140px',
                    height: '36px',
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={16} sx={{
                      color: '#2196f3',
                      marginRight: 1
                    }} />
                  ) : (
                    <>
                      <ContentCopyIcon sx={{ mr: 1 }} />
                      Copy Signature
                    </>
                  )}
                </Button>
              )}
            </Box>

            {index === 2 && selectedTab === 3 && (
              <Box sx={{ backgroundColor: '#fffae8', padding: 2, marginTop: 2, borderRadius: 1, display: 'flex', }}>
                <InfoOutlinedIcon sx={{ marginRight: 2 }} />
                <Typography sx={{ fontSize: '0.9rem' }}>
                  Your email signature won't be fully visible in the preview after you paste it in. It will still come through correctly, however, when sending emails.
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        handleClose={handleCloseNotification}
        severity={notification.severity}
        duration={5000}
      />
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="add-to-email-signature-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(255, 255, 255, 0.5)'
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <Box sx={overlayStyle} />
          <Box sx={modalStyle}>
            <IconButton onClick={onClose} sx={closeButtonStyle}>
              <CloseIcon />
            </IconButton>
            <Typography id="add-to-email-signature-title" variant="h5" component="h2" gutterBottom>
              How to add a signature to your emails
            </Typography>
            <Typography id="add-to-email-signature-description" sx={{ mb: 3 }}>
              Select your email provider below and follow the steps to add your signature
            </Typography>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="email provider tabs"
              centered
              sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', borderBottom: 'none' }}
            >
              {stepsContent.map((provider, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '60px', justifyContent: 'flex-start' }}>
                      {index === 0 ? <GmailIcon /> : <OutlookIcon />}
                      <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: '10px', textAlign: 'left' }}>
                        <Typography sx={{ fontsize: '0.1rem', color: "#000000" }} variant="body1">
                          {provider.title}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={tabStyle}
                />
              ))}
            </Tabs>
            {renderContent()}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AddToEmailSignatureModal;